import { ApiUsageChart } from "@/components/api-usage-chart";
import { CardApiCreditsOverview } from "@/components/api-credits/card-api-credits-overview";
import { DialogPurchaseApiCredits } from "@/components/api-credits/dialog-purchase-api-credits";
import { ButtonLoading } from "@/components/button-loading";
import DataTableTeamApiKeys from "@/components/data-table-team-api-keys/data-table-team-api-keys";
import { DatePickerWithRange } from "@/components/date-range-picker";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import { useTranslation } from "@/hooks/useTranslation";
import {
  ApiUsageResponseDTO,
  TeamApiKeyCreateDTO,
  TeamApiKeyDTO,
  TeamCreditsStackDTO,
} from "@shaarp/common";
import { addDays } from "date-fns";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  ArrowLeft,
  Coins,
  Copy,
  ExternalLink,
  Plus,
  Zap,
} from "lucide-react";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import useAccess from "@/hooks/useAccess";
import { useCallback, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useNavigate } from "react-router-dom";

export default function TeamIntegrationApiKeysPage() {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const [, copyToClipboard] = useCopyToClipboard();
  const navigate = useNavigate();
  const { hasAccess } = useAccess();

  const hasRoleAccess = hasAccess({
    requiredRoles: [TeamUserRolesEnum.MANAGER, TeamUserRolesEnum.ADMIN],
  });

  // API keys state
  const [apiKeys, setApiKeys] = useState<TeamApiKeyDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<TeamApiKeyDTO | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKey, setNewKey] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  // Usage state
  const [usageData, setUsageData] = useState<ApiUsageResponseDTO | null>(null);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: addDays(new Date(), -7),
    to: new Date(),
  });

  // API credits state
  const [apiCreditsOverview, setApiCreditsOverview] = useState<{
    balance: number;
    stacks: TeamCreditsStackDTO[];
  } | null>(null);
  const [isLoadingOverview, setIsLoadingOverview] = useState(true);
  const [isApiCreditsDialogOpen, setIsApiCreditsDialogOpen] = useState(false);

  const fetchApiKeys = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get<TeamApiKeyDTO[]>("/my/team/api-keys");
      setApiKeys(data);
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const fetchApiCreditsOverview = async () => {
    try {
      setIsLoadingOverview(true);
      const { data } = await api.get("/my/team/api-credits/overview");
      setApiCreditsOverview(data);
    } catch {
      // Silently fail, overview is supplementary
    } finally {
      setIsLoadingOverview(false);
    }
  };

  const fetchUsage = useCallback(async () => {
    if (!dateRange.from || !dateRange.to) return;

    try {
      setIsLoadingUsage(true);
      const { data } = await api.get<ApiUsageResponseDTO>(
        `/my/team/api-usage?from=${dateRange.from.toISOString()}&to=${dateRange.to.toISOString()}`
      );
      setUsageData(data);
    } catch (error) {
      toastError();
    } finally {
      setIsLoadingUsage(false);
    }
  }, [dateRange.from, dateRange.to]);

  useEffect(() => {
    fetchApiKeys();
    fetchUsage();
    fetchApiCreditsOverview();
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get("checkout");

    if (checkoutStatus === "success") {
      sessionStorage.removeItem("stripe-api-checkout-package");

      toast({
        title: t("team.apiCredits.purchase.successTitle"),
        description: t("team.apiCredits.purchase.successDescription"),
      });

      // Remove checkout param from URL
      params.delete("checkout");
      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
      window.history.replaceState({}, "", newUrl);

      // Refresh with delay for webhook processing
      void fetchApiCreditsOverview();
      void fetchUsage();
      setTimeout(() => {
        void fetchApiCreditsOverview();
        void fetchUsage();
      }, 2000);
    } else if (checkoutStatus === "cancelled") {
      sessionStorage.removeItem("stripe-api-checkout-package");
      const params = new URLSearchParams(window.location.search);
      params.delete("checkout");
      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      setIsCreating(true);
      const { data } = await api.post<TeamApiKeyCreateDTO>(
        "/my/team/api-keys",
        {
          name: newKeyName.trim(),
        }
      );

      setNewKey(data.key);
      setCreateDialogOpen(false);
      setShowKeyDialog(true);
      setNewKeyName("");

      toast({
        variant: "default",
        title: t("team.apiKeys.dialog.create.success.title"),
        description: t("team.apiKeys.dialog.create.success.description"),
      });

      await fetchApiKeys();
    } catch (error) {
      toastError();
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteKey = async (id: string) => {
    try {
      setIsDeleting(true);
      await api.delete(`/my/team/api-key/${id}`);
      await fetchApiKeys();
      toast({
        variant: "default",
        title: t("team.apiKeys.dialog.delete.success.title"),
        description: t("team.apiKeys.dialog.delete.success.description"),
      });
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
    } catch (error) {
      toastError();
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (apiKey: TeamApiKeyDTO) => {
    setKeyToDelete(apiKey);
    setDeleteDialogOpen(true);
  };

  const handleCopyKey = async () => {
    await copyToClipboard(newKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const handleDateChange = (range: DateRange | null) => {
    if (range?.from && range?.to) {
      setDateRange(range);
    }
  };

  const openBuyDialog = () => {
    setIsApiCreditsDialogOpen(true);
  };

  return (
    <>
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/integrations")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Button>
      </div>

      {/* Credits + Stats — single row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <CardApiCreditsOverview
          balance={apiCreditsOverview?.balance ?? 0}
          isLoading={isLoadingOverview}
          onBuyCredits={openBuyDialog}
        />
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
              <Coins className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t("team.apiKeys.usage.totalCreditsConsumed")}
              </p>
              <p className="text-2xl font-bold">
                {isLoadingUsage ? "..." : (usageData?.totalCreditsConsumed ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <Zap className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t("team.apiKeys.usage.totalRequests")}
              </p>
              <p className="text-2xl font-bold">
                {isLoadingUsage ? "..." : (usageData?.totalRequests ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Chart */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between pb-4 space-y-0">
          <div>
            <CardTitle>{t("team.apiKeys.usage.title")}</CardTitle>
          </div>
          <DatePickerWithRange
            defaultDate={dateRange}
            onChange={handleDateChange}
          />
        </CardHeader>
        <CardContent>
          <ApiUsageChart
            data={usageData?.usage ?? []}
            isLoading={isLoadingUsage}
          />
        </CardContent>
      </Card>

      {/* API Keys Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4 space-y-0">
          <div>
            <CardTitle>{t("team.apiKeys.title")}</CardTitle>
            <CardDescription>{t("team.apiKeys.description")}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {!hasRoleAccess && (
              <Badge
                variant="secondary"
                className="flex gap-1.5 items-center px-3 py-1"
              >
                <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                <span className="text-xs">
                  {t("team.apiKeys.memberRestriction")}
                </span>
              </Badge>
            )}
            <Button variant="outline" asChild>
              <a
                href={import.meta.env.VITE_API_PUBLIC_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t("team.apiKeys.documentationButton")}
              </a>
            </Button>
            <Button
              onClick={() => setCreateDialogOpen(true)}
              disabled={!hasRoleAccess}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("team.apiKeys.createButton")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DataTableTeamApiKeys
            apiKeys={apiKeys}
            isLoading={isLoading}
            onDelete={hasRoleAccess ? openDeleteDialog : undefined}
          />
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("team.apiKeys.dialog.create.title")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                {t("team.apiKeys.dialog.create.nameLabel")}
              </Label>
              <Input
                id="name"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder={t("team.apiKeys.dialog.create.namePlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
            >
              {t("team.apiKeys.dialog.create.cancel")}
            </Button>
            <ButtonLoading
              type="button"
              onClick={handleCreateKey}
              disabled={!newKeyName.trim() || isCreating}
              isLoading={isCreating}
            >
              {t("team.apiKeys.dialog.create.create")}
            </ButtonLoading>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show API Key Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("team.apiKeys.dialog.showKey.title")}</DialogTitle>
            <DialogDescription>
              {t("team.apiKeys.dialog.showKey.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex items-center space-x-2">
              <Input value={newKey} readOnly className="font-mono text-sm" />
              <Button onClick={handleCopyKey} size="sm">
                <Copy className="w-4 h-4 mr-2" />
                {keyCopied
                  ? t("team.apiKeys.dialog.showKey.copied")
                  : t("team.apiKeys.dialog.showKey.copyButton")}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowKeyDialog(false)}>
              {t("team.apiKeys.dialog.showKey.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("team.apiKeys.dialog.delete.title", {
                name: keyToDelete?.name || "",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("team.apiKeys.dialog.delete.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setKeyToDelete(null);
              }}
              disabled={isDeleting}
            >
              {t("team.apiKeys.dialog.delete.cancel")}
            </Button>
            <ButtonLoading
              type="button"
              variant="destructive"
              onClick={() => keyToDelete && handleDeleteKey(keyToDelete.id)}
              disabled={isDeleting}
              isLoading={isDeleting}
            >
              {t("team.apiKeys.dialog.delete.confirm")}
            </ButtonLoading>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Credits Purchase Dialog */}
      <DialogPurchaseApiCredits
        open={isApiCreditsDialogOpen}
        onOpenChange={setIsApiCreditsDialogOpen}
      />
    </>
  );
}
